import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '__generated__/globalTypes';
import { Button } from 'atoms/buttons/Button';
import { Horizontal } from 'atoms/layout/flex';
import { Text16 } from 'atoms/typography';
import { CheckboxFilters } from 'components/collections/filters/CheckboxFilters';
import { StartedOnlyFilter } from 'components/collections/filters/StartedOnlyFilter';
import {
  CollectionsFilterType,
  CollectionsFiltersState,
  RarityCount,
  SeasonCount,
} from 'components/collections/filters/types';
import Dialog from 'components/dialog';
import { filters, glossary } from 'lib/glossary';

const Footer = styled(Horizontal)`
  padding: var(--triple-unit);
`;
const Body = styled.div`
  padding: 0 var(--triple-unit);
`;

type Props = {
  open: boolean;
  filtersState: CollectionsFiltersState;
  raritiesCount: RarityCount[];
  seasonsCount: SeasonCount[];
  hide?: CollectionsFilterType[];
  onClose: () => void;
  onFiltersChange: (newState: CollectionsFiltersState) => void;
};

export const MobileCollectionsFilters = ({
  open,
  filtersState,
  raritiesCount,
  seasonsCount,
  hide,
  onClose,
  onFiltersChange,
}: Props) => {
  /* On mobile the filters are not immediately applied until the user confirms.
  This is the state for the filters that have been selected but not necessarily applied */
  const [selectedFiltersState, setSelectedFiltersState] = useState<
    Omit<CollectionsFiltersState, 'query'>
  >({
    rarities: filtersState.rarities,
    seasonStartYears: filtersState.seasonStartYears,
    startedOnly: filtersState.startedOnly,
  });

  const onRaritiesChange = (newRarities: Rarity[] | undefined) => {
    setSelectedFiltersState({ ...selectedFiltersState, rarities: newRarities });
  };
  const onSeasonsChange = (newSeasons: number[] | undefined) => {
    setSelectedFiltersState({
      ...selectedFiltersState,
      seasonStartYears: newSeasons,
    });
  };
  const onStartedOnlyChange = (startedOnly: boolean) => {
    setSelectedFiltersState({ ...selectedFiltersState, startedOnly });
  };
  const onClearAll = () => {
    setSelectedFiltersState({
      rarities: undefined,
      seasonStartYears: undefined,
      startedOnly: false,
    });
  };
  const onSubmitFilters = () => {
    onFiltersChange({ ...selectedFiltersState, query: filtersState.query });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onBack={onClose}
      title={
        <Text16 bold className="text-center">
          <FormattedMessage {...filters.filters} />
        </Text16>
      }
      footer={
        <Footer>
          <Button
            color="red"
            onClick={onClearAll}
            fullWidth
            size="medium"
            disabled={
              !selectedFiltersState.rarities &&
              !selectedFiltersState.seasonStartYears
            }
          >
            <FormattedMessage {...filters.clearAll} />
          </Button>

          <Button
            color="primary"
            onClick={onSubmitFilters}
            fullWidth
            size="medium"
          >
            <FormattedMessage {...glossary.save} />
          </Button>
        </Footer>
      }
    >
      <Body>
        <CheckboxFilters
          rarities={selectedFiltersState.rarities}
          raritiesCount={raritiesCount}
          seasonStartYears={selectedFiltersState.seasonStartYears}
          seasonsCount={seasonsCount}
          hide={hide}
          onRaritiesChange={onRaritiesChange}
          onSeasonsChange={onSeasonsChange}
        />
        {!hide?.includes(CollectionsFilterType.startedOnly) && (
          <StartedOnlyFilter
            startedOnly={selectedFiltersState.startedOnly}
            onChange={onStartedOnlyChange}
          />
        )}
      </Body>
    </Dialog>
  );
};
